import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, User, ArrowRight, Tag, ArrowUpRight } from 'lucide-react';
import Header from '@/components/public/Header';
import Footer from '@/components/public/Footer';
import { BlogListingSEO } from '@/components/seo/BlogListingSEO';
import { GuidesEnquiryModal } from '@/components/public/GuidesEnquiryModal';
import '@/components/public/uk/uk.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASE_URL = 'https://starteaseai.com';
const POSTS_PER_PAGE = 9;

const CATEGORIES = [
  { value: 'all', label: 'All Posts' },
  { value: 'sales-growth', label: 'Sales & Growth' },
  { value: 'people-payroll', label: 'People & Payroll' },
  { value: 'finance-operations', label: 'Finance & Operations' },
  { value: 'technology-ai', label: 'Technology & AI' },
  { value: 'formation', label: 'Business Formation' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'tax', label: 'Tax & Finance' },
  { value: 'guides', label: 'Guides' },
  { value: 'news', label: 'News & Updates' },
];

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
};

const readingTime = (post) => post.reading_time || post.reading_time_minutes;
const badgeCls = 'text-[10.5px] font-semibold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20';
const categoryLabel = (value) => CATEGORIES.find((c) => c.value === value)?.label || 'Guides';

const CoverImage = ({ post, className }) => (
  post.featured_image
    ? <img src={post.featured_image} alt={post.title} className={className} loading="lazy" />
    : <div className={`${className} flex items-center justify-center bg-[#0e1a15]`}><span className="text-5xl font-serif text-white/10">S</span></div>
);

const FeaturedCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group block rounded-lg overflow-hidden border border-white/10 bg-[#111c17] hover:border-emerald-500/40 transition-colors duration-300"
    data-testid="featured-blog-card"
  >
    <div className="grid md:grid-cols-2 gap-0">
      <div className="aspect-video md:aspect-auto md:h-full overflow-hidden">
        <CoverImage post={post} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-8 lg:p-10 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-5">
          <span className={badgeCls}>{categoryLabel(post.category)}</span>
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.15em] text-emerald-400">Featured</span>
        </div>
        <h2 className="text-2xl md:text-3xl leading-snug mb-4 group-hover:text-emerald-100 transition-colors" style={{ color: '#fff' }}>
          {post.title}
        </h2>
        <p className="text-slate-400 text-sm md:text-base leading-relaxed mb-6 line-clamp-3">
          {post.excerpt || post.meta_description}
        </p>
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
          {post.author && <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{post.author}</span>}
          {post.published_at && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(post.published_at)}</span>}
          {readingTime(post) && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{readingTime(post)} min read</span>}
        </div>
      </div>
    </div>
  </Link>
);

const BlogCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group flex flex-col rounded-lg overflow-hidden border border-white/10 bg-[#111c17] hover:border-emerald-500/40 hover:-translate-y-1 transition-all duration-300"
    data-testid={`blog-card-${post.slug}`}
  >
    <div className="aspect-video overflow-hidden">
      <CoverImage post={post} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    </div>
    <div className="p-6 flex flex-col flex-1">
      <div className="flex items-center gap-2 mb-3">
        <span className={badgeCls}>{categoryLabel(post.category)}</span>
        {readingTime(post) && <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" />{readingTime(post)} min</span>}
      </div>
      <h3 className="text-lg leading-snug mb-2 group-hover:text-emerald-100 transition-colors line-clamp-2" style={{ color: '#fff' }}>
        {post.title}
      </h3>
      <p className="text-slate-400 text-sm leading-relaxed mb-4 line-clamp-2">
        {post.excerpt || post.meta_description}
      </p>
      <div className="flex items-center justify-between pt-4 mt-auto border-t border-white/10">
        <span className="text-xs text-slate-500">{formatDate(post.published_at)}</span>
        <span className="text-emerald-400 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
          Read <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  </Link>
);

const BlogIndexPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPosts, setTotalPosts] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const activeCategory = searchParams.get('category') || 'all';
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const params = { page: currentPage, limit: POSTS_PER_PAGE };
        let endpoint = `${API}/public/blog`;
        if (activeCategory !== 'all') endpoint = `${API}/public/blog/category/${activeCategory}`;
        const response = await axios.get(endpoint, { params });
        setPosts(response.data.posts || []);
        setTotalPosts(response.data.total || 0);
        setError(null);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [currentPage, activeCategory]);

  const handleCategoryChange = (category) => {
    const params = new URLSearchParams();
    if (category !== 'all') params.set('category', category);
    setSearchParams(params);
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) params.set('page', page.toString()); else params.delete('page');
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const featuredPost = posts.length > 0 && currentPage === 1 ? posts[0] : null;
  const regularPosts = currentPage === 1 ? posts.slice(1) : posts;

  const pageTitle = activeCategory !== 'all'
    ? `${CATEGORIES.find(c => c.value === activeCategory)?.label || activeCategory} | Founder Guides | StartEase Agent`
    : 'Founder Guides | StartEase Agent';
  const pageDescription = 'Original founder-led guides across company formation, tax, compliance, sales, payroll, finance and technology — for starting, operating and growing across the US, UK, UAE, Singapore and Canada.';
  const canonical = activeCategory !== 'all' ? `${BASE_URL}/blog/category/${activeCategory}` : `${BASE_URL}/blog`;

  return (
    <div data-testid="blog-index-page">
      <BlogListingSEO title={pageTitle} description={pageDescription} canonical={canonical} posts={posts} currentPage={currentPage} totalPages={totalPages} />
      <Header />

      <div className="uk-page">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#070d0b] border-b border-white/10" data-testid="blog-hero">
          <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full opacity-40 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(5,150,105,0.22), transparent 68%)' }} />
          <div className="container-custom relative py-20 md:py-28 max-w-3xl">
            <span className="uk-eyebrow mb-6" style={{ color: '#6ee7b7' }}>
              <span className="uk-rule" />Founder Guides
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6">
              Guides to start, operate<br className="hidden sm:block" /> and <em style={{ fontStyle: 'italic', color: '#34d399' }}>grow</em>.
            </h1>
            <p className="text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Original, founder-led guides across formation, tax and compliance, plus sales, payroll,
              finance and technology — practical, no fluff, and grounded in official sources.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <span><span className="text-emerald-400 font-semibold">6</span> solution areas</span>
              <span className="w-px h-3 bg-white/15" />
              <span><span className="text-emerald-400 font-semibold">5</span> markets</span>
              <span className="w-px h-3 bg-white/15" />
              <span>Updated with official sources</span>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="bg-[#0b1310] border-b border-white/10" data-testid="category-filter">
          <div className="container-custom">
            <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => handleCategoryChange(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
                    activeCategory === cat.value
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`category-btn-${cat.value}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Posts */}
        <section className="py-14 md:py-20" data-testid="blog-posts-section">
          <div className="container-custom">
            {loading ? (
              <div className="flex items-center justify-center py-24"><div className="animate-pulse text-slate-400">Loading posts…</div></div>
            ) : error ? (
              <div className="text-center py-24">
                <p className="text-rose-400 mb-4">{error}</p>
                <button onClick={() => window.location.reload()} className="uk-text-link">Try again</button>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-24">
                <Tag className="w-12 h-12 text-white/15 mx-auto mb-4" />
                <h3 className="text-xl mb-2" style={{ color: '#fff' }}>No posts found</h3>
                <p className="text-slate-400 mb-6">{activeCategory !== 'all' ? 'No posts in this category yet. Check back soon.' : 'No posts published yet. Check back soon.'}</p>
                {activeCategory !== 'all' && <button onClick={() => handleCategoryChange('all')} className="uk-text-link mx-auto">View all posts</button>}
              </div>
            ) : (
              <>
                {featuredPost && <div className="mb-12"><FeaturedCard post={featuredPost} /></div>}
                {regularPosts.length > 0 && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 mb-14">
                    {regularPosts.map((post) => <BlogCard key={post.id || post.slug} post={post} />)}
                  </div>
                )}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2" data-testid="pagination">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                      className="px-4 py-2 rounded border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Previous</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button key={page} onClick={() => handlePageChange(page)}
                        className={`w-10 h-10 rounded font-medium transition-colors ${page === currentPage ? 'bg-emerald-600 text-white' : 'border border-white/10 text-slate-300 hover:bg-white/5'}`}>{page}</button>
                    ))}
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="pb-20" data-testid="newsletter-cta">
          <div className="container-custom">
            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#111c17] px-8 py-14 md:py-16 text-center">
              <div className="absolute -bottom-24 -left-16 w-[420px] h-[420px] rounded-full opacity-30 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(5,150,105,0.28), transparent 68%)' }} />
              <div className="relative max-w-2xl mx-auto">
                <span className="uk-eyebrow justify-center mb-5" style={{ color: '#6ee7b7' }}><span className="uk-rule" />Ready when you are</span>
                <h2 className="text-2xl md:text-3xl mb-4" style={{ color: '#fff' }}>Turn a guide into a plan</h2>
                <p className="text-slate-400 mb-8">Bring us a short brief and we'll map the right structure across your markets — one relationship, every filing.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button type="button" onClick={() => setEnquiryOpen(true)} className="uk-button" data-testid="cta-consultation">Request a consultation<ArrowUpRight className="w-4 h-4" /></button>
                  <Link to="/login" className="uk-button uk-button-outline" data-testid="cta-signup">Get started free</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <GuidesEnquiryModal open={enquiryOpen} onOpenChange={setEnquiryOpen} />
      <Footer />
    </div>
  );
};

export default BlogIndexPage;
